/**
 * GuideCategoryPage — one help-centre category at /help/guides/:category.
 *
 * Categories are built from the VISIBLE article set, so a category whose guides are all
 * staff-facing simply doesn't exist for a patient — same "not found" state as an unknown
 * slug (404-not-403: don't confirm what exists).
 */

import React, { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { List, Typography } from "antd";
import Layout from "../../../components/Layout";
import { PageContainer, SectionCard, EmptyState } from "../../../components/shared";
import { articles, categoryDefs } from "../../../content/help";
import { visibleArticles, buildCategories } from "../../../content/help/model";
import { isPatientPortalUser } from "../../../utils/userPrefs/validatePermissions";

const { Text } = Typography;

const GuideCategoryPage: React.FC = () => {
  const { category: categorySlug } = useParams<{ category: string }>();
  const navigate = useNavigate();

  const viewerIsPatient = isPatientPortalUser();
  const visible = useMemo(() => visibleArticles(articles, viewerIsPatient), [viewerIsPatient]);
  const categories = useMemo(() => buildCategories(visible, categoryDefs), [visible]);
  const category = categories.find((c) => c.slug === categorySlug);

  if (!category) {
    return (
      <Layout>
        <PageContainer
          title="User Guides"
          breadcrumbs={[{ label: "Help & Support", path: "/help" }, { label: "User Guides" }]}
          data-testid="help-guides-category"
        >
          <EmptyState
            title="Section not found"
            description="It may have moved, or it isn't available on your account."
            action={{ label: "Back to guides", onClick: () => navigate("/help/guides") }}
          />
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageContainer
        title={category.title}
        subtitle={category.summary}
        breadcrumbs={[
          { label: "Help & Support", path: "/help" },
          { label: "User Guides", path: "/help/guides" },
          { label: category.title },
        ]}
        data-testid="help-guides-category"
      >
        <SectionCard>
          <List
            itemLayout="vertical"
            dataSource={category.articles}
            renderItem={(article) => (
              <List.Item key={article.slug} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <Link
                  to={article.path}
                  data-testid={`help-guides-article-link-${article.slug}`}
                  style={{ fontSize: 14, fontWeight: 600 }}
                >
                  {article.title}
                </Link>
                <div style={{ marginTop: 2 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {article.summary}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        </SectionCard>
      </PageContainer>
    </Layout>
  );
};

export default GuideCategoryPage;
